import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useEntitlements } from "../components/DataContext/EntitlementsContext";
import { ChildApp, applyDevEntryOverrides, loadChildApps } from "./appList";
import { preloadApps } from "./loadApp";

export type ChildAppsStatus = "idle" | "loading" | "ready" | "error";

interface ChildAppsContextType {
  apps: ChildApp[];
  status: ChildAppsStatus;
  /** Fetches the list again (fresh signed links) and returns this slug's registration, if still granted. */
  refresh: (slug: string) => Promise<ChildApp | undefined>;
}

const ChildAppsContext = createContext<ChildAppsContextType | undefined>(undefined);

// Loads the child-app list once the user is signed in and AMP's own
// entitlements are in. Loading earlier would ask for a token before there is an
// account, and getAuthToken signs the user out when it finds none. A failed
// list leaves AMP's own tabs untouched: the user just sees no child apps.
export const ChildAppsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accounts } = useMsal();
  const { hasLoadedOnce } = useEntitlements();
  const signedIn = accounts.length > 0 && hasLoadedOnce;
  const [apps, setApps] = useState<ChildApp[]>([]);
  const [status, setStatus] = useState<ChildAppsStatus>("idle");

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    setStatus("loading");
    loadChildApps()
      .then((fromEntitlements) => {
        if (cancelled) return;
        setApps(fromEntitlements);
        setStatus("ready");
        preloadApps(fromEntitlements);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn("Child app list could not be loaded", error);
        // In development an override can still supply an app (no-op elsewhere).
        const local = applyDevEntryOverrides([]);
        setApps(local);
        setStatus(local.length > 0 ? "ready" : "error");
        preloadApps(local);
      });
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  const refresh = useCallback(async (slug: string) => {
    // Same fallback as the first load: while entitlements fails (no JSON, 4xx, 5xx), the
    // dev override still resolves instead of the retry throwing.
    const list = await loadChildApps().catch(() => applyDevEntryOverrides([]));
    setApps(list);
    return list.find((app) => app.slug === slug);
  }, []);

  const value = useMemo(() => ({ apps, status, refresh }), [apps, status, refresh]);
  return <ChildAppsContext.Provider value={value}>{children}</ChildAppsContext.Provider>;
};

export const useChildApps = () => {
  const context = useContext(ChildAppsContext);
  if (!context) throw new Error("useChildApps must be used within a ChildAppsProvider");
  return context;
};
